import { useState, useCallback, useMemo } from 'react';
import Helmet from 'react-helmet';

import { Loader2 } from 'lucide-react';
import { Button, FormGroup, Label, Input, cn } from 'twyne-new.ui';
import { isEmail } from 'validator';

import HeaderContainerGrid from '@/components/headerContainer/Grid';
import { ReportBody } from '@/components/reports/body';
import { HeaderActions } from '@/components/reports/header';
import { formatters } from '@Components/hierarchical-report/Layout';
import Configure from '@Components/hierarchical-report/configure';

import rewardsStoreLegacy from '../../stores/reward'

import RewardCompletions from './components/RewardCompletions';
import { RewardsReportProvider, useRewardsReportStore } from './store';

function Search({ onSearch, email, onChangeEmail, onChangeEmailIsValid, emailIsValid }) {
  const handleChangeEmail = useCallback(e => {
    const validEmail = isEmail(email) && !email.includes("test@qa.com")
    onChangeEmailIsValid(validEmail)
    onChangeEmail(e.target.value)
  }, [email, onChangeEmail, onChangeEmailIsValid])

  return <>
    <div className="w-1/2">
      <FormGroup>
        <Label>Email</Label>
        <div className="flex gap-4">
          <Input value={email}
            className={cn(!emailIsValid && 'is-invalid', 'rounded-r-none')}
            type="email"
            placeholder="Enter email"
            onInput={handleChangeEmail}
          />
          <Button
            color='accent'
            className="rounded-l-none"
            onClick={() => onSearch()}
            disabled={rewardsStoreLegacy.isLoading || !emailIsValid || !email}
          >
            {rewardsStoreLegacy.isLoading ? <Loader2 size='14' className='animate-spin' /> : 'Search'}
          </Button>
        </div>
      </FormGroup>
    </div>
  </>
}

function Result({ data, updateState, email, emailIsValid }) {
  if (!emailIsValid && email) {
    return <div className='twy-text-danger font-semi invalid-email'>Please enter a valid email</div>
  }

  if (data?.length === 0) {
    return <div className='font-semibold mt-4'>No results to show.</div>
  }

  if (!email || data === null) {
    return null
  }

  return <RewardCompletions data={data} updateState={updateState} />
}

function Content() {
  const [results, setResults] = useState(null)
  const [filterby, setFilterby] = useState({})
  const [email, setEmail] = useState('')
  const [emailIsValid, setEmailIsValid] = useState(true)

  const rewardsStore = useRewardsReportStore()
  const { report: { useLoad }, reportView } = rewardsStore
  useLoad()

  const updateState = useCallback((completionId, status) => {
    setResults(results => results.map(completion => completion.id === completionId ? { ...completion, status } : completion))
  }, [])

  const configure = useMemo(() => ({
    filterby: {
      options: [
        {
          value: 'rewardId',
          get label() {
            return <formatters.Loadable isLoading={rewardsStoreLegacy.reward.isLoading}>Reward</formatters.Loadable>
          },
          options: rewardsStoreLegacy.reward.fetchData,
        },
        {
          value: 'status',
          get label() {
            return <formatters.Loadable>Status</formatters.Loadable>
          },
          options: async () => [
            { value: "active", label: "Active", getLabel: () => "Active", getValue: () => "active" },
            { value: "ignited", label: "Ignited", getLabel: () => "Ignited", getValue: () => "ignited" },
            { value: "completed", label: "Completed", getLabel: () => "Completed", getValue: () => "completed" },
          ],
        },
      ],
    },
  }), [])

  const handleSearch = () => {
    if (emailIsValid && email) {
      rewardsStoreLegacy.getRewards({ email, filterby }).then(data => {
        setResults(data)
      })
    }
  }

  return <>
    <Helmet title="Rewards" />
    <HeaderContainerGrid title='Rewards Report'>
      <HeaderActions>
        <Configure
          key='filter'
          store={reportView}
          configure={configure}
          onApply={({ filterby }) => {
            setFilterby(filterby)
            return true
          }}
          value={{ filterby }}
        />
      </HeaderActions>
    </HeaderContainerGrid>
    <ReportBody contentClassName='p-4'>
      <Search onSearch={handleSearch} email={email} onChangeEmail={setEmail} onChangeEmailIsValid={setEmailIsValid} emailIsValid={emailIsValid} />
      <Result data={results} updateState={updateState} email={email} emailIsValid={emailIsValid} />
    </ReportBody>
  </>
}

export default function RewardsLayout() {
  return <RewardsReportProvider>
    <Content />
  </RewardsReportProvider>
}
